import type { ReactNode } from 'react'
import palette from '../series.module.css'
import styles from './ChartFigure.module.css'

/**
 * The frame every chart in this console is drawn inside, and the reason any of
 * them are readable without eyes.
 *
 * A chart is a picture of a table. This component insists on the table.
 *
 * The usual accessible chart is an SVG with `role="img"` and an `aria-label`
 * summarising it: "Bar chart of questions by week, rising". That sentence is
 * written by a person, at the time the component is built, about data that
 * arrives later, and it goes out of date on the first Tuesday the numbers move.
 * It also cannot carry twelve values, so the one thing a reader might actually
 * want, the numbers, is the one thing it leaves out.
 *
 * Here the picture is `aria-hidden` and a real `<table>` of the same array sits
 * next to it, rendered from the same props the marks were drawn from, so the
 * two cannot drift. It costs a few hundred bytes of DOM. In exchange the data
 * is readable by a screen reader, findable by the browser's own find-in-page,
 * selectable, and copyable into a spreadsheet, and `tableVisible` turns it into
 * a feature for everybody rather than an accommodation for one group.
 */

export interface ChartTableRow {
  /** The row header. A category, a date, a series name. */
  header: string
  cells: readonly string[]
}

export interface ChartTable {
  /** Column headings. The first names the row-header column itself. */
  columns: readonly string[]
  rows: readonly ChartTableRow[]
}

export interface ChartFigureProps {
  /** What the chart shows. Becomes the figure's caption and the table's. */
  title: string
  /** The units, the period, the caveat. Sits under the title. */
  description?: ReactNode
  table: ChartTable
  titleVisible?: boolean
  tableVisible?: boolean
  legend?: ReactNode
  /** The SVG. */
  children: ReactNode
  className?: string
}

export function ChartFigure({
  title,
  description,
  table,
  titleVisible = true,
  tableVisible = false,
  legend,
  children,
  className,
}: ChartFigureProps) {
  return (
    <figure className={[styles.figure, className].filter(Boolean).join(' ')}>
      <figcaption className={titleVisible ? styles.caption : 'visually-hidden'}>
        <span className={styles.title}>{title}</span>
        {description ? <span className={styles.description}>{description}</span> : null}
      </figcaption>

      {/* The drawing carries no information that is not in the table below it. */}
      <div className={styles.plot} aria-hidden="true">
        {children}
      </div>

      {legend}

      <div className={tableVisible ? styles.tableWrap : 'visually-hidden'}>
        <table className={styles.table}>
          {/*
            Hidden even when the table is shown, because the figcaption above is
            already saying it. Present always, because a table reached directly
            by a screen reader's table navigation has no figcaption in earshot.
          */}
          <caption className="visually-hidden">{title}</caption>
          <thead>
            <tr>
              {table.columns.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={`${row.header}-${rowIndex}`}>
                <th scope="row">{row.header}</th>
                {row.cells.map((cell, index) => (
                  <td key={table.columns[index + 1] ?? index}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  )
}

export interface LegendEntry {
  label: string
  /** An optional figure, for charts where the legend is also the readout. */
  value?: string
}

/**
 * The key.
 *
 * `aria-hidden`, and that is deliberate rather than lazy. Every word in it
 * appears verbatim in the table below as a column or row heading, so leaving it
 * exposed makes a screen reader read the series names once as a list and again
 * as headings, and the second reading is the useful one.
 */
export function ChartLegend({ entries }: { entries: readonly LegendEntry[] }) {
  return (
    <ul className={palette.legend} aria-hidden="true">
      {entries.map((entry, index) => (
        <li key={entry.label} className={palette.legendItem}>
          {/* The series colour is on the swatch and not on the row, because the
              row is text: the chart palette is measured to clear 3:1 as a mark
              and nothing here promises it clears 4.5:1 as a label. */}
          <span className={[palette.series, palette.swatch].join(' ')} data-series={index} />
          <span>{entry.label}</span>
          {entry.value ? <span className={palette.legendValue}>{entry.value}</span> : null}
        </li>
      ))}
    </ul>
  )
}
